import type { Price } from './price'

/**
 * UID-402 Gate-5 W2 (ADR-0124) — the SINGLE owner of the staging-triage
 * priceability and confirmability predicates, shared by `apps/api`
 * (`buildStagingAnalysisSignal`, which projects them onto the wire as
 * `StagingAnalysisSignal`) and `apps/web` (`verticals/uploads/staging-cta.ts`,
 * which drives the Triage page's primary CTA and its confirm batches).
 *
 * ## Why this lives here rather than in either app
 *
 * These rules were previously implemented THREE times: once server-side, and
 * twice client-side (once for the CTA, once — hand-rolled, per call site — for
 * the confirm filter). That duplication was not hypothetical debt, it shipped a
 * real bug: the client copies both omitted the `matchType === 'manual'` guard
 * (ADR-0066), and because `confirmParts` is all-or-nothing, one demo match made
 * the entire "Review in library" hand-off 409 — reintroducing the very defect
 * UID-402 existed to fix.
 *
 * A parity TEST would only have detected drift after the fact. One owner makes
 * the drift unrepresentable, which is the whole point. This follows the
 * precedent ADR-0117 D2 set for the `PartProcess` enum and the manufacturing
 * catalog: `packages/shared` owns logic both apps need, specifically so
 * `apps/api` never has to import from `apps/web`.
 *
 * ## Why the parameter types are structural
 *
 * Both apps hand-declare their own `IdentifiedPart` / `MatchedPart` mirroring
 * the same wire contract (see each file's doc comment on why they mirror rather
 * than import). Rather than force one app's nominal type on the other — or move
 * those whole interfaces here, which would be a far larger change than the
 * predicates justify — each function takes the MINIMAL structural shape it
 * actually reads. Both apps' richer types satisfy these automatically.
 */

/** The part-type badge vocabulary (`StagedPartType` in both apps). */
export type StagedPartTypeLike = 'catalog' | 'configurable' | 'custom'

/** The match-quality vocabulary (`MatchType` in both apps). */
export type StagedMatchTypeLike = 'exact' | 'close' | 'partial' | 'manual'

/** The matched-part lifecycle (`MatchedPartStatus` in both apps). */
export type StagedMatchStatusLike = 'matched' | 'confirmed' | 'ignored'

/** The only two fields {@link isStagedPartPriceable} reads. */
export interface StagedPartPriceabilityFields {
  readonly type: StagedPartTypeLike
  readonly indicativePrice: Price | null
}

/**
 * The price shape {@link isStagedMatchPriceable} reads off a sourcing option.
 * Structural, and deliberately WIDER than `Price` (`provenance` is `string`,
 * not `PriceProvenance`) for the same reason the rest of this file is
 * structural: both apps pass their own `Price` and neither type leaks in.
 *
 * The four fields ARE the price quad `migrations/1002:467` constrains with
 * `num_nonnulls(price_amount, price_currency, price_basis, price_provenance)
 * IN (0,4)` — all four or none. The predicate must not be looser than the
 * constraint the database already enforces, which is why it checks all four
 * rather than merely `price !== null`.
 */
export interface StagedPriceLike {
  readonly amount: string
  readonly currency: string
  readonly basis: string
  readonly provenance: string
}

/**
 * The two fields {@link isStagedMatchPriceable} reads off each sourcing option.
 *
 * ADR-0145 D3 — this used to be `unknown`, because only the ARRAY LENGTH
 * mattered. It no longer does: the predicate must see the SELECTED option's
 * price, so the option payload is now (minimally) part of the contract. Both
 * apps' richer `SourcingOption` satisfies it structurally, unchanged.
 */
export interface StagedSourcingOptionLike {
  readonly selected: boolean
  readonly price: StagedPriceLike | null
}

/** The fields {@link isStagedMatchPriceable} reads. */
export interface StagedMatchPriceabilityFields {
  readonly matchType: StagedMatchTypeLike
  readonly sourcingOptions: readonly StagedSourcingOptionLike[]
}

/**
 * WHICH sourcing option a matched part's claim rests on: the one the user
 * selected, else the first offered.
 *
 * The SINGLE owner of that selection rule, and it has to be, because
 * `isStagedMatchPriceable` (the guard) and `matchedPartToLibraryPart` (the
 * mapper that stamps the resulting claim) must never disagree about which
 * option is being attested — a guard that inspects one option while the mapper
 * publishes another is the same class of defect as a duplicated predicate.
 * Generic so the mapper gets its OWN richer option type back, with vendor and
 * SKU intact.
 */
export function selectedStagedSourcingOption<T extends { readonly selected: boolean }>(
  options: readonly T[],
): T | undefined {
  return options.find((o) => o.selected) ?? options[0]
}

/**
 * Is this a COMPLETE price — all four quad fields populated?
 *
 * Mirrors `migrations/1002:467`'s `num_nonnulls(...) IN (0,4)`: a price is
 * absent or whole, never half. Deliberately says NOTHING about which
 * `provenance` the price carries — a `parametric` price is a real price, and
 * whether it may back a `misumi_standard` claim is the AUTHORITY question owned
 * by `matchedPartToLibraryPart` (ADR-0145 D3's no-coercion MUST), not the
 * priceability question. Flattening the two would demote every genuinely priced
 * non-catalog match.
 */
export function isCompleteStagedPrice(price: StagedPriceLike | null | undefined): boolean {
  if (price == null) return false
  return (
    price.amount.trim() !== '' &&
    price.currency.trim() !== '' &&
    price.basis.trim() !== '' &&
    price.provenance.trim() !== ''
  )
}

/**
 * Is this price one that may back an AUTHORITATIVE `misumi_standard` claim?
 *
 * The SINGLE owner of the ADR-0066 authority rule, extracted by ADR-0162 D2.6
 * step 2 (UID-496) because it now has TWO readers and must not have two copies:
 *
 *  1. `matchedPartToLibraryPart` (apps/api), which REFUSES with
 *     `reason: 'non-catalog-price'` and is the enforcement point;
 *  2. {@link isStagedMatchConfirmable} below, which must PREDICT that refusal
 *     so a caller cannot compose a batch the server will reject.
 *
 * WHY (2) NEEDED IT, and this is a defect this extraction closes rather than a
 * tidy-up: `isStagedMatchConfirmable` never checked provenance. It did not have
 * to while it also refused every catalog-arm part outright — that blanket
 * clause MASKED the gap. Removing the clause (D2.6 step 2) made the gap
 * REACHABLE: the catalog arm demonstrably yields complete `parametric` prices
 * on a `partner` supplier, so a leaf would have been reported confirmable,
 * passed `applyConfirmParts` clause M5 (which checks completeness, not
 * authority), and then thrown at attestation — 409-ing the ENTIRE all-or-nothing
 * batch (ADR-0060) and taking every legitimate part with it. Exactly the defect
 * shape ADR-0162 D2.6 declares merge-blocking, arriving by the attestation door
 * instead of clause M1.
 *
 * NOT FOLDED INTO {@link isStagedMatchPriceable}, deliberately, and this is
 * ADR-0145 D3's split held to: a complete `parametric` price is REAL MONEY and
 * that part IS priceable. Claiming otherwise would be the predicate-weakening
 * inversion D3 forbids. Authority is a different question from money, so it
 * lives on CONFIRMABILITY — which is exactly where the catalog-arm clause it
 * replaces also lived, and for the same reason.
 *
 * Completeness is required too (via {@link isCompleteStagedPrice}): a half
 * price with `provenance: 'catalog'` is not authoritative, it is malformed.
 */
export function isCatalogAuthoritativeStagedPrice(
  price: StagedPriceLike | null | undefined,
): boolean {
  return isCompleteStagedPrice(price) && price?.provenance === 'catalog'
}

/** The only field {@link isStagedPartConfirmable} reads. */
export interface StagedPartConfirmabilityFields {
  readonly type: StagedPartTypeLike
}

/**
 * {@link isStagedMatchConfirmable} additionally reads `status`.
 *
 * `partId` USED TO BE REQUIRED HERE, for the resolution-arm clause ADR-0162
 * D2.6 step 2 removed (see {@link isStagedMatchConfirmable}). It is dropped
 * rather than left in place, because a required field the predicate no longer
 * reads is a false statement about what this contract needs — and the comment
 * that justified it cited ADR-0145 D1, which rules the `cat:` NAMESPACE and
 * says nothing about confirmability (a half-phantom citation, ADR-0162 D1.3).
 *
 * Dropping it is safe for every caller: these shapes are STRUCTURAL, so both
 * apps' richer `MatchedPart` (which still carries `partId`, for its own
 * reasons) continues to satisfy this interface unchanged.
 */
export interface StagedMatchConfirmabilityFields extends StagedMatchPriceabilityFields {
  readonly status: StagedMatchStatusLike
}

/**
 * Can the system price this identified part with NO human step?
 *
 * `catalog` ⇒ yes, unconditionally: a catalog part is priceable by CATALOG
 * LOOKUP, so a null `indicativePrice` is normal for it and does NOT make it
 * unpriceable. **Do not add an `indicativePrice` guard to this branch** — it
 * would demote every legitimately price-less catalog row to manual.
 *
 * Otherwise (`configurable` / `custom`) it is priceable only once configured,
 * which is exactly what carrying an `indicativePrice` means.
 *
 * NOTE what is deliberately absent: `drawingFilename`. A missing drawing is an
 * acceptable state, not a pricing blocker (UID-402 — the same rule that removed
 * the amber missing-drawing tint from the Triage row).
 */
export function isStagedPartPriceable(part: StagedPartPriceabilityFields): boolean {
  if (part.type === 'catalog') return true
  return part.indicativePrice !== null
}

/**
 * Can the system price this matched part with NO human step?
 *
 * Three clauses, all ADR-0066 trust-boundary rules:
 *  - a `manual` match is a user-picked DEMO candidate and must never be treated
 *    as authoritative catalog pricing;
 *  - a match with no sourcing option has no vendor/SKU/price to price with;
 *  - **the SELECTED option must carry a COMPLETE price** (ADR-0145 D3).
 *
 * ## Why the third clause exists (ADR-0145 D3 — the finding)
 *
 * This predicate used to be `matchType !== 'manual' && sourcingOptions.length >
 * 0`: it tested that an option EXISTS, never that the option carries a PRICE.
 * That was sufficient only by ACCIDENT — `applyResolveFile` always wrote
 * `sourcingOptions: []`, so the length clause carried the whole boundary
 * (ADR-0144 R21's accidental control). Compose the old predicate with the
 * catalog contract instead — `CatalogItem.indicativePrice` is `Price | null`
 * ("null when priced only after quoting", `catalog-search-port.ts:137`) — and
 * the moment a real catalog populates `sourcingOptions` you get: vendor + SKU
 * but no price ⇒ an option exists ⇒ the guard passes ⇒ confirm succeeds ⇒ a
 * Library part stamped `provenance: 'misumi_standard'` with NO PRICE. That is
 * the ADR-0066 harm on the SUCCESS path. So the guard is STRENGTHENED here, at
 * the single owner, BEFORE the accidental control is removed.
 *
 * The price is checked on the SELECTED option
 * ({@link selectedStagedSourcingOption}) — the one whose claim would be
 * published — not on "some option", and it must be COMPLETE
 * ({@link isCompleteStagedPrice}, mirroring the DB's all-or-none CHECK).
 *
 * Callers must exclude `ignored` parts before asking (an ignored match is a
 * resolved, set-aside state — neither priceable nor outstanding). For the
 * combined question, use {@link isStagedMatchConfirmable}.
 */
export function isStagedMatchPriceable(part: StagedMatchPriceabilityFields): boolean {
  return (
    part.matchType !== 'manual' &&
    isCompleteStagedPrice(selectedStagedSourcingOption(part.sourcingOptions)?.price)
  )
}

/**
 * Can this matched part be sent to `confirmParts` without 409-ing the batch?
 *
 * `confirmParts` is ALL-OR-NOTHING (ADR-0060): one unconfirmable part rejects
 * every part in the request, so any caller building a confirm batch MUST filter
 * with this first.
 *
 * Mirrors `applyConfirmParts` (`apps/api/src/modules/parts-library/
 * staging-store.ts`) clause for clause. That function rejects a part that is:
 *   1. not present in the session — caller-side non-issue (callers only ever
 *      pass ids read out of the triage they hold);
 *   2. `status === 'ignored'`;
 *   3. `matchType === 'manual'` — rejected EXPLICITLY on match type server-side,
 *      deliberately not as a side effect of clause 4, so that a manual match
 *      which ever gained sourcing options still cannot be written as an
 *      authoritative Library part. This is the clause the pre-W1 client copies
 *      omitted;
 *   4. `sourcingOptions.length === 0`;
 *   5. the selected option carries no COMPLETE price (ADR-0145 D3).
 *
 * Composed from {@link isStagedMatchPriceable} rather than restating clauses 3,
 * 4 and 5. That confirmability and priceability coincide is intentional and
 * documented server-side ("a matched part is auto-quote exactly when it is
 * confirmable as-is") — but they answer different questions, one about money and
 * one about a durable write, so they stay separately named.
 *
 * ## ADR-0145 D3 — why the strengthening went into `isStagedMatchPriceable`
 * ## and NOT here as well
 *
 * ## The RESOLUTION-ARM clause — REMOVED by ADR-0162 D2.6 step 2 (UID-496)
 *
 * This predicate used to carry `&& !isCatalogLeafPartId(part.partId)`, refusing
 * every catalog-arm part (`partId` in the `cat:` namespace — a BOM line naming
 * a MISUMI SKU with no uploaded CAD). **The clause is gone, on the exact
 * condition its own removal note specified.**
 *
 * WHY IT WAS THERE. Not money: a catalog leaf with a complete
 * `provenance: 'catalog'` price IS priceable, and `matchedPartToLibraryPart`
 * correctly attests it, so the ADR-0066 trust boundary was already satisfied
 * (`staging-service.catalog-leaf.test.ts` asserts exactly that, positively).
 * What was missing was the durable WRITE PATH: both `StagingStore` bindings
 * persist the triage as `StagedFileRow`s and a catalog leaf has no
 * `intake.staged_file` row (migration 1013's second resolution arm), so
 * `applyConfirmParts` clause M1 — "not present in the session" — refused it
 * structurally. Mirroring that refusal here kept a caller from composing a
 * batch the server would reject, which is precisely this predicate's job.
 *
 * WHY IT IS GONE. The removal note read: *"REMOVE THIS CLAUSE when the catalog
 * arm gains a durable write path (a `staged_bom_node`-keyed match state, or a
 * `library.part`-derived `confirmed` status)."* Migration **1021** adds
 * `intake.staged_bom_node.match_confirmed_at` — the first of those two, and the
 * one ADR-0162 D2.1/D2.2 ruled correct after showing the `library.part`-derived
 * alternative was unsound (that key is account-scoped and many-to-one with
 * nodes, so one confirm would have leaked across every session and every
 * same-SKU BOM line). So the condition is met by the ruled-for design, not by
 * the rejected one.
 *
 * ORDER MATTERS, AND IT IS MERGE-BLOCKING (ADR-0162 D2.6). Removing this clause
 * in isolation would be WORSE than leaving it: `confirmParts` is all-or-nothing
 * (ADR-0060), so a batch the CTA composed with a catalog leaf in it would 404 on
 * clause M1 and take every legitimate part with it. The clause may only come out
 * once the verb can SEE catalog leaves — which is why `StagingStore.confirmParts`
 * gained its `catalogLeaves` parameter in the same change. **If you are reading
 * this because a merge conflict tempted you to restore the clause: restore the
 * SERVER change with it, or neither.**
 *
 * WHAT STILL REFUSES A CATALOG LEAF, so this is not a blanket opening: an
 * ignored one, via the `status !== 'ignored'` clause below composed with
 * ADR-0162 D3.5(1)'s derivation of that status from `match_ignored_at`; an
 * unattested or incompletely-priced one, via `isStagedMatchPriceable`; and one
 * with no selected option at all, likewise. Only the ARM itself stopped being
 * a refusal.
 *
 * ## Why the COMPLETE-PRICE strengthening went into `isStagedMatchPriceable`
 * ## and NOT here as well
 *
 * The complete-price requirement is a statement about MONEY: without a whole
 * price there is nothing to price the line with, which is precisely the
 * priceability question. Adding a price clause HERE too would be a second
 * executable copy of it — the exact structural defect ADR-0124 Gate-5 W1
 * records and this file exists to prevent. Confirmability inherits the
 * strengthening for free BY COMPOSITION, which is the whole reason it is
 * composed rather than restated. What stays this predicate's own is the one
 * clause that is genuinely about the durable WRITE and not about money:
 * `status !== 'ignored'`.
 */
export function isStagedMatchConfirmable(part: StagedMatchConfirmabilityFields): boolean {
  return (
    part.status !== 'ignored' &&
    isStagedMatchPriceable(part) &&
    // ADR-0162 D2.6 step 2 — mirrors `matchedPartToLibraryPart`'s
    // `non-catalog-price` refusal through the ONE shared owner of that rule,
    // never a second copy of it. This clause REPLACES the blanket catalog-arm
    // clause: what actually made an unattestable part unconfirmable was always
    // the authority question, and the arm was standing in for it.
    isCatalogAuthoritativeStagedPrice(selectedStagedSourcingOption(part.sourcingOptions)?.price)
  )
}

/**
 * UID-425 (A2) — can this IDENTIFIED part be sent to `confirmParts` without
 * 409-ing the batch?
 *
 * `confirmParts` is ALL-OR-NOTHING (ADR-0060), and it now materialises
 * identified parts as well as matched ones (closing ADR-0066 Deferrals item 1),
 * so any caller building a confirm batch MUST filter its identified ids with
 * this the same way it filters matched ids with
 * {@link isStagedMatchConfirmable}. This is the SINGLE owner of the rule — the
 * structural mistake ADR-0124 Gate-5 W1 records is a confirm contract
 * hand-rolled per call site, so do not restate these clauses anywhere else.
 *
 * Mirrors `applyConfirmParts` (`apps/api/src/modules/parts-library/
 * staging-store.ts`) clause for clause. That function rejects an identified part
 * that is:
 *   1. not present in the session's `identified[]` — caller-side non-issue
 *      (callers only ever pass ids read out of the triage they hold), and the
 *      reason this predicate takes no id;
 *   2. `type === 'catalog'`. A catalog staged part is a GLOBAL Sourceable
 *      carrying `misumi_standard` provenance; materialising it through the
 *      identified path would mint a tenant-owned `custom` Sourceable with
 *      `fictiv_custom` provenance for MISUMI catalog data — a false provenance
 *      claim, which is the boundary ADR-0066 drew. Catalog parts reach the
 *      Library through the MATCHED path (`matchedPartToLibraryPart`) or not at
 *      all.
 *
 * WHAT IS DELIBERATELY ABSENT, and why each absence is a real ruling:
 *   - **No `status` clause.** `IdentifiedPart` has no lifecycle status to be
 *     `ignored` in — the identified section's "set aside" gesture is
 *     `removePart` (UID-222 #7/#8), which DELETES the row, so an ignored
 *     identified part is not a representable state. The `status` this ticket
 *     ADDS (`identified | confirmed`) is not a guard either: a re-confirm must
 *     be IDEMPOTENT (it is what lets a user press "Create RFQ" twice), not a
 *     conflict.
 *   - **No `configuration` clause.** An unconfigured part is exactly what this
 *     ticket exists to hand over; requiring a configuration would reinstate
 *     ADR-0125 D1's disjointness bug in a new place.
 *   - **No `indicativePrice` clause.** Confirmability is a question about a
 *     durable WRITE, not about money. Unlike the matched pair, this predicate
 *     does NOT compose {@link isStagedPartPriceable}: an unconfigured custom
 *     part is unpriceable AND confirmable, and that asymmetry is the fix.
 */
export function isStagedPartConfirmable(part: StagedPartConfirmabilityFields): boolean {
  return part.type !== 'catalog'
}
